/**
 * The light top bar.
 *
 * Search (Ctrl+K) leads, because jumping to a screen or an account is the
 * most frequent thing done from here. Then today's date in both calendars,
 * language, light/dark, and the user menu. On a phone it also carries the
 * menu button that opens the sidebar as a drawer.
 */
import { useState } from 'react'
import {
  Box, ButtonBase, Divider, IconButton, ListItemIcon, Menu, MenuItem, Tooltip,
  Typography,
} from '@mui/material'
import MenuOpenIcon from '@mui/icons-material/Menu'
import SearchIcon from '@mui/icons-material/Search'
import DarkModeIcon from '@mui/icons-material/DarkModeOutlined'
import LightModeIcon from '@mui/icons-material/LightModeOutlined'
import TranslateIcon from '@mui/icons-material/Translate'
import LogoutIcon from '@mui/icons-material/Logout'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../store/AuthContext'
import { useLedgers } from '../store/LedgerCache'
import { useThemeMode } from '../store/ThemeMode'
import { setLanguage } from '../i18n'
import { gregorianLong, toHijri } from '../reports/hijri'
import { today } from '../dates'
import { c } from '../theme'
import Keycap from './Keycap'

export function DatePair() {
  const { i18n } = useTranslation()
  const iso = today()
  const pill = {
    fontSize: 12, fontWeight: 500, px: 1.25, py: 0.6, borderRadius: 999,
    whiteSpace: 'nowrap' as const,
  }
  return (
    <Box sx={{ display: { xs: 'none', lg: 'flex' }, gap: 0.75 }}>
      <Box component="span" sx={{ ...pill, bgcolor: c.surfaceSunken, color: c.inkSecondary,
                                  border: `1px solid ${c.border}` }}>
        {gregorianLong(iso, i18n.language)}
      </Box>
      <Box component="span" lang="ar" dir="rtl" sx={{
        ...pill, bgcolor: c.primarySoft, color: c.primaryInk,
        fontFamily: 'var(--font-arabic)' }}>
        {toHijri(iso, 'ar')}
      </Box>
    </Box>
  )
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  const s = parts.length > 1 ? parts[0][0] + parts[1][0] : name.slice(0, 2)
  return s.toUpperCase()
}

export default function TopBar(
  { onMenu, onSearch }: { onMenu: () => void; onSearch: () => void },
) {
  const { t, i18n } = useTranslation()
  const { me, logout } = useAuth()
  const { ledgers, loading } = useLedgers()
  const { mode, toggle } = useThemeMode()
  const [anchor, setAnchor] = useState<null | HTMLElement>(null)
  const ar = i18n.language === 'ar'
  const who = me?.full_name || me?.username || ''
  const themeLabel = mode === 'dark' ? t('theme.toLight') : t('theme.toDark')

  const company = me?.companies.find((x) => x.id === me.company_id)
  const companyName = (ar ? company?.name_ar : null) || company?.name_en || ''

  return (
    <Box component="header" sx={{
      position: 'sticky', top: 0, zIndex: 1100,
      // Clears the notch when installed to the home screen.
      pt: 'env(safe-area-inset-top)',
      bgcolor: c.surface, borderBottom: `1px solid ${c.border}`,
      '@media print': { display: 'none' },
    }}>
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 },
      minHeight: 60, px: { xs: 1, sm: 2, md: 4 },
    }}>
      <IconButton onClick={onMenu} aria-label={t('shell.openMenu')}
        sx={{ display: { md: 'none' }, color: c.ink }}>
        <MenuOpenIcon />
      </IconButton>

      <ButtonBase onClick={onSearch} aria-label={t('shell.search')}
        aria-keyshortcuts="Control+K"
        sx={{
          flex: '1 1 auto', maxWidth: 440, minWidth: 0, minHeight: 40, px: 1.5, gap: 1.25,
          justifyContent: 'flex-start', borderRadius: '8px',
          border: `1px solid ${c.border}`, bgcolor: c.surfaceSunken, color: c.inkMuted,
          fontSize: 14,
          '&:focus-visible': { outline: '2px solid var(--focus-ring)', outlineOffset: 2 },
        }}>
        <SearchIcon fontSize="small" aria-hidden />
        <Box component="span" sx={{ flex: 1, textAlign: 'start', whiteSpace: 'nowrap',
                                    overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {t('shell.searchPlaceholder')}
        </Box>
        <Keycap combo="ctrl+k" />
      </ButtonBase>

      <Box sx={{ flex: 1 }} />

      <DatePair />

      {/* How many accounts are loaded for instant search. Desktop only. */}
      <Tooltip title={loading ? t('common.loading') : t('common.cached', { count: ledgers.length })}>
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 6, height: 6, borderRadius: 6,
                     bgcolor: loading ? c.inkMuted : c.successInk }} />
          <Typography className="num" sx={{ fontSize: 12, color: c.inkMuted }}>
            {loading ? '···' : ledgers.length}
          </Typography>
        </Box>
      </Tooltip>

      <Box role="group" aria-label={t('shell.language')} sx={{
        display: { xs: 'none', sm: 'flex' }, border: `1px solid ${c.border}`,
        borderRadius: '8px', overflow: 'hidden' }}>
        {(['en', 'ar'] as const).map((l) => {
          const on = i18n.language === l
          return (
            <ButtonBase key={l} aria-pressed={on} onClick={() => setLanguage(l)}
              sx={{ minWidth: 44, minHeight: 36, fontSize: 13,
                    fontWeight: on ? 600 : 400,
                    fontFamily: l === 'ar' ? 'var(--font-arabic)' : undefined,
                    bgcolor: on ? c.primary : c.surface,
                    color: on ? c.onPrimary : c.inkSecondary }}>
              {l === 'en' ? 'EN' : 'ع'}
            </ButtonBase>
          )
        })}
      </Box>

      <Tooltip title={themeLabel}>
        <IconButton onClick={toggle} aria-label={themeLabel}
          sx={{ display: { xs: 'none', sm: 'inline-flex' }, color: c.inkSecondary,
                border: `1px solid ${c.border}`, borderRadius: '8px', width: 40, height: 40 }}>
          {mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
        </IconButton>
      </Tooltip>

      <ButtonBase onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="menu" aria-label={t('shell.userMenu', { name: who })}
        sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: c.primary,
              color: c.onPrimary, fontWeight: 600, fontSize: 13, flexShrink: 0,
              '&:focus-visible': { outline: '2px solid var(--focus-ring)', outlineOffset: 2 } }}>
        {initials(who || '?')}
      </ButtonBase>

      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: ar ? 'left' : 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: ar ? 'left' : 'right' }}>
        <Box sx={{ px: 2, py: 1, minWidth: 220 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{who}</Typography>
          <Typography sx={{ fontSize: 12, color: c.inkMuted }}>
            {me?.username}{me?.role ? ` · ${me.role}` : ''}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={() => { toggle(); setAnchor(null) }}>
          <ListItemIcon>
            {mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
          </ListItemIcon>
          {themeLabel}
        </MenuItem>
        <MenuItem onClick={() => { setLanguage(ar ? 'en' : 'ar'); setAnchor(null) }}>
          <ListItemIcon><TranslateIcon fontSize="small" /></ListItemIcon>
          {ar ? 'English' : 'العربية'}
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { setAnchor(null); logout() }}>
          <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
          {t('auth.signOut')}
        </MenuItem>
      </Menu>
    </Box>

    {/* Below the desktop width the sidebar (and its company switcher) is
        hidden, so the open company is named here instead: HiFi runs three
        companies, and figures read under the wrong one are wrong figures. */}
    <ButtonBase onClick={onMenu} aria-label={t('shell.switchCompany', { name: companyName })}
      sx={{
        display: { xs: 'flex', md: 'none' }, width: '100%', justifyContent: 'flex-start',
        gap: 1, px: 2, py: 0.75, borderTop: `1px solid ${c.divider}`,
        fontSize: 12, color: c.inkSecondary, textAlign: 'start',
      }}>
      <Box component="span" sx={{ fontWeight: 600, color: c.ink, whiteSpace: 'nowrap',
                                  overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {companyName}
      </Box>
      <Box component="span" sx={{ color: c.inkMuted, whiteSpace: 'nowrap' }}>
        {t('shell.finYear', { year: me?.fin_year ?? '' })}
      </Box>
    </ButtonBase>
    </Box>
  )
}
